/**
 * Mountain Lake Quest Definitions
 *
 * All curated quests for the Mountain Lake zone, organized into two chains:
 *   1. "Gone Fishin'" — Tutorial campaign (~10 quests, teaches every mechanic)
 *   2. "Lake Legend"  — Zone progression arc (~18 quests, unlocks Mountain Stream)
 *
 * NPC Quest Givers:
 *   - Reel:       Your overly enthusiastic best friend. Boundlessly optimistic, terrible at fishing.
 *   - Old Pete:   Grizzled Lakeside Tackle owner. Seen it all. Perpetually unimpressed.
 *   - Chef Marco: Dramatic camp cook. WAY too passionate about fish freshness.
 *   - Wanda:      Mysterious lake regular. Speaks in riddles. Knows all the lake's secrets.
 */

import {
  QuestDefinition,
  QuestChain,
  QuestType,
  QuestObjectiveType,
  QuestRewardType,
  Quality,
} from '@/types';

// ─── Quest Chain Definitions ───────────────────────────────────────────

export const TUTORIAL_CHAIN: QuestChain = {
  id: 'chain_tutorial',
  name: 'Gone Fishin\'',
  description: 'Learn the ropes with your buddy Reel, grumpy Old Pete, and the enthusiastic Chef Marco.',
  icon: '🎣',
  color: '#EAB308',
  displayOrder: 0,
};

export const LAKE_LEGEND_CHAIN: QuestChain = {
  id: 'chain_lake_legend',
  name: 'Lake Legend',
  description: 'From nobody to Mountain Lake master. Prove yourself and unlock new waters.',
  icon: '🏔️',
  color: '#3B82F6',
  displayOrder: 1,
};

export const DEFAULT_QUEST_CHAINS: QuestChain[] = [
  TUTORIAL_CHAIN,
  LAKE_LEGEND_CHAIN,
];

// ═══════════════════════════════════════════════════════════════════════
// CHAIN 1: GONE FISHIN' (Tutorial Campaign)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Quest 1: Just Add Water
 * First cast — catch literally anything
 */
export const TUT_FIRST_CAST: QuestDefinition = {
  id: 'tut_first_cast',
  name: 'Just Add Water',
  description: 'Reel hands you a rod that looks older than Old Pete. "Okay, step one: put the hook in the water. Step two: hope for the best. I believe in you, buddy!"',
  type: QuestType.CATCH_QUANTITY,
  giverNpc: 'Reel',

  objectives: [
    {
      id: 'obj_first_fish',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch any fish',
      targetValue: 1,
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 50, description: '+50 gold' },
  ],

  unlockConditions: {},
  repeatable: false,
  isTutorial: true,
  category: 'tutorial',
  chainId: TUTORIAL_CHAIN.id,
  displayOrder: 0,
  nextQuestId: 'tut_sweet_spot',
};

/**
 * Quest 2: Timing is Everything
 * Sweet spot mechanic tutorial
 */
export const TUT_SWEET_SPOT: QuestDefinition = {
  id: 'tut_sweet_spot',
  name: 'Timing is Everything',
  description: 'Reel squints at your casting technique. "See that glowy bit on the ring? That\'s the sweet spot. Hit it and the fish practically jumps into your hands. Miss it and... well, you\'ve seen me fish."',
  type: QuestType.CATCH_QUANTITY,
  giverNpc: 'Reel',

  objectives: [
    {
      id: 'obj_timing',
      type: QuestObjectiveType.TIMING_HIT,
      description: 'Land 3 Perfect or Good timing hits',
      targetValue: 3,
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 40, description: '+40 gold' },
  ],

  unlockConditions: {
    completedQuests: ['tut_first_cast'],
  },
  repeatable: false,
  isTutorial: true,
  category: 'tutorial',
  chainId: TUTORIAL_CHAIN.id,
  displayOrder: 1,
  nextQuestId: 'tut_first_sale',
};

/**
 * Quest 3: Marco's Menu
 * Selling fish tutorial
 */
export const TUT_FIRST_SALE: QuestDefinition = {
  id: 'tut_first_sale',
  name: "Marco's Menu",
  description: 'Chef Marco peers at your catch with theatrical intensity. "Ah, a fish! Fresh from the lake, yes? Bring it to me. I will make it into something... magnificent. Also I will pay you. Mostly I will pay you."',
  type: QuestType.SELL_SPECIES,
  giverNpc: 'Chef Marco',

  objectives: [
    {
      id: 'obj_sell_fish',
      type: QuestObjectiveType.SELL_FISH,
      description: 'Sell any fish at the market',
      targetValue: 1,
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 75, description: '+75 gold' },
  ],

  unlockConditions: {
    completedQuests: ['tut_sweet_spot'],
  },
  repeatable: false,
  isTutorial: true,
  category: 'tutorial',
  chainId: TUTORIAL_CHAIN.id,
  displayOrder: 2,
  nextQuestId: 'tut_gear_up',
};

/**
 * Quest 4: Old Pete's Discount Rack
 * Equipment upgrade tutorial
 */
export const TUT_GEAR_UP: QuestDefinition = {
  id: 'tut_gear_up',
  name: "Old Pete's Discount Rack",
  description: 'Pete slides a slightly-less-terrible rod across the counter. "Your current rod is an insult to fish everywhere. This one\'s only a mild insult. Fifty gold. And that\'s the friends-and-family price \u2014 you\'re neither, but you look desperate."',
  type: QuestType.UNLOCK_EQUIPMENT,
  giverNpc: 'Old Pete',

  objectives: [
    {
      id: 'obj_buy_rod',
      type: QuestObjectiveType.BUY_EQUIPMENT,
      description: 'Purchase a Reinforced Rod',
      targetValue: 1,
      filters: {
        equipmentId: 'rod_reinforced',
      },
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 50, description: '+50 gold' },
    { type: QuestRewardType.EXPERIENCE, value: 25, description: '+25 XP' },
  ],

  unlockConditions: {
    completedQuests: ['tut_first_sale'],
    minCurrency: 50,
  },
  repeatable: false,
  isTutorial: true,
  category: 'tutorial',
  chainId: TUTORIAL_CHAIN.id,
  displayOrder: 3,
  nextQuestId: 'tut_bait_basics',
};

/**
 * Quest 5: Worm Welcome
 * Bait usage tutorial
 */
export const TUT_BAIT_BASICS: QuestDefinition = {
  id: 'tut_bait_basics',
  name: 'Worm Welcome',
  description: 'Pete holds up a container of worms. "See these? Fish love \'em. Equip some bait and watch your catch chance go up. The worms aren\'t thrilled about the arrangement, but nobody asked them."',
  type: QuestType.CATCH_QUANTITY,
  giverNpc: 'Old Pete',

  objectives: [
    {
      id: 'obj_catch_with_bait',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch 3 fish',
      targetValue: 3,
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 60, description: '+60 gold' },
  ],

  unlockConditions: {
    completedQuests: ['tut_gear_up'],
    ownedEquipment: ['bait_worms'],
  },
  repeatable: false,
  isTutorial: true,
  category: 'tutorial',
  chainId: TUTORIAL_CHAIN.id,
  displayOrder: 4,
  nextQuestId: 'tut_buddy_system',
};

/**
 * Quest 6: The Buddy System
 * Fishing buddy tutorial
 */
export const TUT_BUDDY_SYSTEM: QuestDefinition = {
  id: 'tut_buddy_system',
  name: 'The Buddy System',
  description: 'Reel\'s eyes light up. "Okay, hear me out \u2014 what if we hired someone to fish FOR us while we also fish? Double the fish! I\'m basically a genius." He is not a genius. But he\'s not wrong.',
  type: QuestType.UNLOCK_BUDDY,
  giverNpc: 'Reel',

  objectives: [
    {
      id: 'obj_hire_buddy',
      type: QuestObjectiveType.HIRE_BUDDY,
      description: 'Hire your first fishing buddy',
      targetValue: 1,
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 100, description: '+100 gold' },
    { type: QuestRewardType.INVENTORY_EXPANSION, value: 5, description: '+5 inventory slots' },
  ],

  unlockConditions: {
    completedQuests: ['tut_bait_basics'],
    minCurrency: 100,
  },
  repeatable: false,
  isTutorial: true,
  category: 'tutorial',
  chainId: TUTORIAL_CHAIN.id,
  displayOrder: 5,
  nextQuestId: 'tut_hauler_hustle',
};

/**
 * Quest 7: Hauler Hustle
 * Hauler buddy tutorial
 */
export const TUT_HAULER_HUSTLE: QuestDefinition = {
  id: 'tut_hauler_hustle',
  name: 'Hauler Hustle',
  description: 'Marco wipes his hands on his apron. "Listen, I love when you visit. But what if someone brought the fish TO me? A hauler buddy! They carry, I cook, you fish. Everyone wins. Especially me. I get fish."',
  type: QuestType.UNLOCK_BUDDY,
  giverNpc: 'Chef Marco',

  objectives: [
    {
      id: 'obj_hire_hauler',
      type: QuestObjectiveType.HIRE_BUDDY,
      description: 'Hire your first hauler buddy',
      targetValue: 1,
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 150, description: '+150 gold' },
    { type: QuestRewardType.INVENTORY_EXPANSION, value: 5, description: '+5 inventory slots' },
  ],

  unlockConditions: {
    completedQuests: ['tut_buddy_system'],
    minCurrency: 500,
  },
  repeatable: false,
  isTutorial: true,
  category: 'tutorial',
  chainId: TUTORIAL_CHAIN.id,
  displayOrder: 6,
  nextQuestId: 'tut_fly_fishing',
};

/**
 * Quest 8: Fly Me to the Trout
 * Fly fishing tutorial
 */
export const TUT_FLY_FISHING: QuestDefinition = {
  id: 'tut_fly_fishing',
  name: 'Fly Me to the Trout',
  description: 'Pete strokes his beard thoughtfully. "You ever try fly fishing? It\'s an art form. You trace the line through the air like poetry in motion. ...Or in your case, like a cat attacking a laser pointer. Either way, buy a fly rod and give it a shot."',
  type: QuestType.CATCH_QUANTITY,
  giverNpc: 'Old Pete',

  objectives: [
    {
      id: 'obj_fly_catch',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch a fish with a fly rod',
      targetValue: 1,
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 100, description: '+100 gold' },
    { type: QuestRewardType.EXPERIENCE, value: 50, description: '+50 XP' },
  ],

  unlockConditions: {
    completedQuests: ['tut_hauler_hustle'],
    ownedEquipment: ['rod_fly_bamboo'],
  },
  repeatable: false,
  isTutorial: true,
  category: 'tutorial',
  chainId: TUTORIAL_CHAIN.id,
  displayOrder: 7,
  nextQuestId: 'tut_trap_life',
};

/**
 * Quest 9: It's a Trap!
 * Trapping tutorial
 */
export const TUT_TRAP_LIFE: QuestDefinition = {
  id: 'tut_trap_life',
  name: "It's a Trap!",
  description: 'Pete pulls a wire basket from under the counter. "Not everything worth catching takes a hook. Set a trap, wait a bit, pull it up, and boom \u2014 crawdads. The lazy fisherman\'s best friend. Don\'t tell anyone I said that."',
  type: QuestType.CATCH_QUANTITY,
  giverNpc: 'Old Pete',

  objectives: [
    {
      id: 'obj_set_trap',
      type: QuestObjectiveType.SET_TRAP,
      description: 'Set a trap',
      targetValue: 1,
    },
    {
      id: 'obj_pull_trap',
      type: QuestObjectiveType.PULL_TRAP,
      description: 'Pull a trap',
      targetValue: 1,
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 75, description: '+75 gold' },
    { type: QuestRewardType.EXPERIENCE, value: 25, description: '+25 XP' },
  ],

  unlockConditions: {
    completedQuests: ['tut_fly_fishing'],
    ownedEquipment: ['trap_wire_basket'],
  },
  repeatable: false,
  isTutorial: true,
  category: 'tutorial',
  chainId: TUTORIAL_CHAIN.id,
  displayOrder: 8,
  nextQuestId: 'tut_dig_deep',
};

/**
 * Quest 10: Dig It
 * Worm digging tutorial
 */
export const TUT_DIG_DEEP: QuestDefinition = {
  id: 'tut_dig_deep',
  name: 'Dig It',
  description: 'Reel is covered in dirt. "DUDE. Did you know you can dig for your own worms?! Free bait! Well, free if you don\'t count the dignity you lose crawling around in the mud. Totally worth it though."',
  type: QuestType.CATCH_QUANTITY,
  giverNpc: 'Reel',

  objectives: [
    {
      id: 'obj_dig_worms',
      type: QuestObjectiveType.DIG_WORMS,
      description: 'Dig for worms',
      targetValue: 1,
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 50, description: '+50 gold' },
  ],

  unlockConditions: {
    completedQuests: ['tut_trap_life'],
  },
  repeatable: false,
  isTutorial: true,
  category: 'tutorial',
  chainId: TUTORIAL_CHAIN.id,
  displayOrder: 9,
  nextQuestId: 'lake_ten_and_counting',
};

// ═══════════════════════════════════════════════════════════════════════
// CHAIN 2: LAKE LEGEND (Zone Progression)
// ═══════════════════════════════════════════════════════════════════════

// ─── Act 1: Small Fish, Big Dreams ─────────────────────────────────────

/**
 * Lake Legend 1: Ten and Counting
 */
export const LAKE_TEN_AND_COUNTING: QuestDefinition = {
  id: 'lake_ten_and_counting',
  name: 'Ten and Counting',
  description: 'Reel slaps you on the back. "Okay, tutorial\'s over! Time to get serious. And by serious I mean... catch ten fish. Baby steps, champ. Baby steps to the boat."',
  type: QuestType.CATCH_QUANTITY,
  giverNpc: 'Reel',

  objectives: [
    {
      id: 'obj_catch_10',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch 10 fish',
      targetValue: 10,
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 200, description: '+200 gold' },
  ],

  unlockConditions: {
    completedQuests: ['tut_dig_deep'],
  },
  repeatable: false,
  isTutorial: false,
  category: 'progression',
  chainId: LAKE_LEGEND_CHAIN.id,
  displayOrder: 0,
  nextQuestId: 'lake_species_sampler',
};

/**
 * Lake Legend 2: The Sampler Platter
 */
export const LAKE_SPECIES_SAMPLER: QuestDefinition = {
  id: 'lake_species_sampler',
  name: 'The Sampler Platter',
  description: 'Marco dramatically unfurls a menu. "I am creating the ULTIMATE lake tasting menu. But I need variety! Bring me different species. I need to taste the rainbow \u2014 the FISH rainbow. Not the candy. Although..."',
  type: QuestType.DISCOVER_SPECIES,
  giverNpc: 'Chef Marco',

  objectives: [
    {
      id: 'obj_species_1',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch a Bluegill',
      targetValue: 1,
      filters: { speciesId: 'bluegill' },
    },
    {
      id: 'obj_species_2',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch a Brook Trout',
      targetValue: 1,
      filters: { speciesId: 'brook_trout' },
    },
    {
      id: 'obj_species_3',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch a Yellow Perch',
      targetValue: 1,
      filters: { speciesId: 'perch' },
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 150, description: '+150 gold' },
    { type: QuestRewardType.EXPERIENCE, value: 50, description: '+50 XP' },
  ],

  unlockConditions: {
    completedQuests: ['lake_ten_and_counting'],
  },
  repeatable: false,
  isTutorial: false,
  category: 'progression',
  chainId: LAKE_LEGEND_CHAIN.id,
  displayOrder: 1,
  nextQuestId: 'lake_quality_control',
};

/**
 * Lake Legend 3: Quality Control
 */
export const LAKE_QUALITY_CONTROL: QuestDefinition = {
  id: 'lake_quality_control',
  name: 'Quality Control',
  description: 'Marco holds a fish to the light, squinting. "This? This is COMMON. Common! Marco does not cook common. Bring me something Uncommon, at least. I have standards. Low standards, but standards."',
  type: QuestType.CATCH_QUALITY,
  giverNpc: 'Chef Marco',

  objectives: [
    {
      id: 'obj_uncommon',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch an Uncommon quality fish (or better)',
      targetValue: 1,
      filters: {
        quality: [Quality.UNCOMMON, Quality.RARE, Quality.EPIC, Quality.LEGENDARY, Quality.UNIQUE],
      },
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 100, description: '+100 gold' },
    { type: QuestRewardType.EXPERIENCE, value: 50, description: '+50 XP' },
  ],

  unlockConditions: {
    completedQuests: ['lake_species_sampler'],
  },
  repeatable: false,
  isTutorial: false,
  category: 'progression',
  chainId: LAKE_LEGEND_CHAIN.id,
  displayOrder: 2,
  nextQuestId: 'lake_bulk_order',
};

/**
 * Lake Legend 4: Bulk Order
 */
export const LAKE_BULK_ORDER: QuestDefinition = {
  id: 'lake_bulk_order',
  name: 'Bulk Order',
  description: 'Marco waves a letter around. "A hiking group is coming through! Twenty hungry hikers! I need fish, fish, and more fish. Sell me ten! Andiamo!"',
  type: QuestType.SELL_SPECIES,
  giverNpc: 'Chef Marco',

  objectives: [
    {
      id: 'obj_sell_10',
      type: QuestObjectiveType.SELL_FISH,
      description: 'Sell 10 fish at the market',
      targetValue: 10,
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 200, description: '+200 gold' },
    { type: QuestRewardType.INVENTORY_EXPANSION, value: 5, description: '+5 inventory slots' },
  ],

  unlockConditions: {
    completedQuests: ['lake_quality_control'],
    minFishSold: 5,
  },
  repeatable: false,
  isTutorial: false,
  category: 'progression',
  chainId: LAKE_LEGEND_CHAIN.id,
  displayOrder: 3,
  nextQuestId: 'lake_big_spender',
};

/**
 * Lake Legend 5: Invest in Yourself
 */
export const LAKE_BIG_SPENDER: QuestDefinition = {
  id: 'lake_big_spender',
  name: 'Invest in Yourself',
  description: 'Pete leans on the counter. "You\'ve been eyeballing that nicer gear, haven\'t you? Go on, treat yourself. A good fisherman invests in their tools. A GREAT fisherman also invests in my shop. Please. Invest in my shop."',
  type: QuestType.SPEND_CURRENCY,
  giverNpc: 'Old Pete',

  objectives: [
    {
      id: 'obj_spend_200',
      type: QuestObjectiveType.SPEND_GOLD,
      description: 'Spend 200 gold on equipment',
      targetValue: 200,
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 100, description: '+100 gold' },
    { type: QuestRewardType.EXPERIENCE, value: 50, description: '+50 XP' },
  ],

  unlockConditions: {
    completedQuests: ['lake_bulk_order'],
    minCurrencyEarned: 500,
  },
  repeatable: false,
  isTutorial: false,
  category: 'progression',
  chainId: LAKE_LEGEND_CHAIN.id,
  displayOrder: 4,
  nextQuestId: 'lake_trout_about',
};

// ─── Act 2: Getting Serious ────────────────────────────────────────────

/**
 * Lake Legend 6: Trout About Town
 */
export const LAKE_TROUT_ABOUT: QuestDefinition = {
  id: 'lake_trout_about',
  name: 'Trout About Town',
  description: 'Wanda gazes at the water. "The trout are restless today. I can feel it. Catch me a Brook Trout and a Cutthroat... the lake wants you to know them by name."',
  type: QuestType.CATCH_SPECIES,
  giverNpc: 'Wanda',

  objectives: [
    {
      id: 'obj_brook',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch a Brook Trout',
      targetValue: 1,
      filters: { speciesId: 'brook_trout' },
    },
    {
      id: 'obj_cutthroat',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch a Cutthroat Trout',
      targetValue: 1,
      filters: { speciesId: 'cutthroat_trout' },
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 200, description: '+200 gold' },
    { type: QuestRewardType.EXPERIENCE, value: 75, description: '+75 XP' },
  ],

  unlockConditions: {
    completedQuests: ['lake_big_spender'],
  },
  repeatable: false,
  isTutorial: false,
  category: 'progression',
  chainId: LAKE_LEGEND_CHAIN.id,
  displayOrder: 5,
  nextQuestId: 'lake_buddy_bonanza',
};

/**
 * Lake Legend 7: Buddy Bonanza
 */
export const LAKE_BUDDY_BONANZA: QuestDefinition = {
  id: 'lake_buddy_bonanza',
  name: 'Buddy Bonanza',
  description: 'Reel is practically vibrating with excitement. "What\'s better than one fishing buddy? TWO fishing buddies! Imagine the haul! Imagine the EFFICIENCY! I\'ve been reading about it online. Okay, I saw a meme about it. Same thing."',
  type: QuestType.UNLOCK_BUDDY,
  giverNpc: 'Reel',

  objectives: [
    {
      id: 'obj_second_buddy',
      type: QuestObjectiveType.HIRE_BUDDY,
      description: 'Hire a second fishing buddy',
      targetValue: 1,
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 200, description: '+200 gold' },
    { type: QuestRewardType.INVENTORY_EXPANSION, value: 5, description: '+5 inventory slots' },
  ],

  unlockConditions: {
    completedQuests: ['lake_trout_about'],
    minBuddyCount: 1,
  },
  repeatable: false,
  isTutorial: false,
  category: 'progression',
  chainId: LAKE_LEGEND_CHAIN.id,
  displayOrder: 6,
  nextQuestId: 'lake_color_collector',
};

/**
 * Lake Legend 8: Fifty Shades of Trout
 */
export const LAKE_COLOR_COLLECTOR: QuestDefinition = {
  id: 'lake_color_collector',
  name: 'Fifty Shades of Trout',
  description: 'Wanda traces patterns in the dirt. "Rainbow... Brown... Cutthroat... Every trout tells a different story. Catch one of each and maybe the lake will tell you yours." She pauses. "Also, they sell well."',
  type: QuestType.CATCH_SPECIES,
  giverNpc: 'Wanda',

  objectives: [
    {
      id: 'obj_rainbow',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch a Rainbow Trout',
      targetValue: 1,
      filters: { speciesId: 'trout' },
    },
    {
      id: 'obj_brown',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch a Brown Trout',
      targetValue: 1,
      filters: { speciesId: 'brown_trout' },
    },
    {
      id: 'obj_cutthroat',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch a Cutthroat Trout',
      targetValue: 1,
      filters: { speciesId: 'cutthroat_trout' },
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 300, description: '+300 gold' },
    { type: QuestRewardType.EXPERIENCE, value: 100, description: '+100 XP' },
  ],

  unlockConditions: {
    completedQuests: ['lake_buddy_bonanza'],
  },
  repeatable: false,
  isTutorial: false,
  category: 'progression',
  chainId: LAKE_LEGEND_CHAIN.id,
  displayOrder: 7,
  nextQuestId: 'lake_craw_daddy',
};

/**
 * Lake Legend 9: Craw Daddy
 */
export const LAKE_CRAW_DADDY: QuestDefinition = {
  id: 'lake_craw_daddy',
  name: 'Craw Daddy',
  description: 'Marco\'s eyes go wide. "You can trap CRAWDADS here?! Forget the fish menu \u2014 I\'m adding a crawdad boil! Bring me five of those little pinchy bois. ...That\'s the culinary term."',
  type: QuestType.CATCH_QUANTITY,
  giverNpc: 'Chef Marco',

  objectives: [
    {
      id: 'obj_catch_crawdads',
      type: QuestObjectiveType.CATCH_CRUSTACEAN,
      description: 'Catch 5 crustaceans from traps',
      targetValue: 5,
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 200, description: '+200 gold' },
    { type: QuestRewardType.EXPERIENCE, value: 50, description: '+50 XP' },
  ],

  unlockConditions: {
    completedQuests: ['lake_color_collector'],
    ownedEquipment: ['trap_wire_basket'],
  },
  repeatable: false,
  isTutorial: false,
  category: 'progression',
  chainId: LAKE_LEGEND_CHAIN.id,
  displayOrder: 8,
  nextQuestId: 'lake_gold_rush',
};

/**
 * Lake Legend 10: Show Me the Money
 */
export const LAKE_GOLD_RUSH: QuestDefinition = {
  id: 'lake_gold_rush',
  name: 'Show Me the Money',
  description: 'Pete checks his ledger. "You know, when I started out, I didn\'t make a thousand gold in my first YEAR. \'Course, gold was worth more back then. And fish were bigger. And it was uphill both ways. But still \u2014 earn a thousand gold and I\'ll be impressed."',
  type: QuestType.EARN_CURRENCY,
  giverNpc: 'Old Pete',

  objectives: [
    {
      id: 'obj_earn_1000',
      type: QuestObjectiveType.EARN_CURRENCY,
      description: 'Earn 1,000 gold total from selling',
      targetValue: 1000,
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 250, description: '+250 gold' },
    { type: QuestRewardType.INVENTORY_EXPANSION, value: 10, description: '+10 inventory slots' },
  ],

  unlockConditions: {
    completedQuests: ['lake_craw_daddy'],
    minCurrencyEarned: 500,
  },
  repeatable: false,
  isTutorial: false,
  category: 'progression',
  chainId: LAKE_LEGEND_CHAIN.id,
  displayOrder: 9,
  nextQuestId: 'lake_bass_to_bass',
};

// ─── Act 3: The Big Leagues ────────────────────────────────────────────

/**
 * Lake Legend 11: Bass to Bass
 */
export const LAKE_BASS_TO_BASS: QuestDefinition = {
  id: 'lake_bass_to_bass',
  name: 'Bass to Bass',
  description: 'Wanda nods toward the deeper water. "There are Largemouth Bass out there. Big ones. Mean ones. The kind that look at your bait and laugh. But you\'re ready. Probably."',
  type: QuestType.CATCH_SPECIES,
  giverNpc: 'Wanda',

  objectives: [
    {
      id: 'obj_catch_bass',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch 2 Largemouth Bass',
      targetValue: 2,
      filters: { speciesId: 'bass' },
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 300, description: '+300 gold' },
    { type: QuestRewardType.EXPERIENCE, value: 100, description: '+100 XP' },
  ],

  unlockConditions: {
    completedQuests: ['lake_gold_rush'],
    minFishCaught: 50,
  },
  repeatable: false,
  isTutorial: false,
  category: 'progression',
  chainId: LAKE_LEGEND_CHAIN.id,
  displayOrder: 10,
  nextQuestId: 'lake_rare_pursuit',
};

/**
 * Lake Legend 12: Rare Air
 */
export const LAKE_RARE_PURSUIT: QuestDefinition = {
  id: 'lake_rare_pursuit',
  name: 'Rare Air',
  description: 'Wanda looks up from the water for the first time all day. "You\'re ready for something special. The Tiger Trout \u2014 part Brook, part Brown, ALL attitude. Striped like a tiger, fights like one too. Catch one and I\'ll tell you a secret about this lake."',
  type: QuestType.CATCH_SPECIES,
  giverNpc: 'Wanda',

  objectives: [
    {
      id: 'obj_tiger_trout',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch a Tiger Trout',
      targetValue: 1,
      filters: { speciesId: 'tiger_trout' },
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 400, description: '+400 gold' },
    { type: QuestRewardType.EXPERIENCE, value: 150, description: '+150 XP' },
  ],

  unlockConditions: {
    completedQuests: ['lake_bass_to_bass'],
  },
  repeatable: false,
  isTutorial: false,
  category: 'progression',
  chainId: LAKE_LEGEND_CHAIN.id,
  displayOrder: 11,
  nextQuestId: 'lake_fly_master',
};

/**
 * Lake Legend 13: Lord of the Flies
 */
export const LAKE_FLY_MASTER: QuestDefinition = {
  id: 'lake_fly_master',
  name: 'Lord of the Flies',
  description: 'Pete adjusts his spectacles. "You\'ve been doing alright with the fly rod, but alright isn\'t good enough. Catch five fish with fly patterns and maybe \u2014 MAYBE \u2014 I\'ll stop cringing when you cast. No promises."',
  type: QuestType.CATCH_QUANTITY,
  giverNpc: 'Old Pete',

  objectives: [
    {
      id: 'obj_fly_5',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch 5 fish with a fly rod',
      targetValue: 5,
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 300, description: '+300 gold' },
    { type: QuestRewardType.EXPERIENCE, value: 100, description: '+100 XP' },
  ],

  unlockConditions: {
    completedQuests: ['lake_rare_pursuit'],
    ownedEquipment: ['rod_fly_bamboo'],
  },
  repeatable: false,
  isTutorial: false,
  category: 'progression',
  chainId: LAKE_LEGEND_CHAIN.id,
  displayOrder: 12,
  nextQuestId: 'lake_supply_chain',
};

/**
 * Lake Legend 14: The Supply Chain
 */
export const LAKE_SUPPLY_CHAIN: QuestDefinition = {
  id: 'lake_supply_chain',
  name: 'The Supply Chain',
  description: 'Marco is pacing. "Business is BOOMING. But I need a steady supply! Your haulers \u2014 put them to work! I need consistent deliveries. Think of it as... a fish subscription service. FishFlix. ...I\'ll work on the name."',
  type: QuestType.SELL_SPECIES,
  giverNpc: 'Chef Marco',

  objectives: [
    {
      id: 'obj_hauler_sell',
      type: QuestObjectiveType.HAULER_SELL_FISH,
      description: 'Have haulers sell 30 fish',
      targetValue: 30,
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 400, description: '+400 gold' },
    { type: QuestRewardType.INVENTORY_EXPANSION, value: 10, description: '+10 inventory slots' },
  ],

  unlockConditions: {
    completedQuests: ['lake_fly_master'],
  },
  repeatable: false,
  isTutorial: false,
  category: 'progression',
  chainId: LAKE_LEGEND_CHAIN.id,
  displayOrder: 13,
  nextQuestId: 'lake_species_master',
};

// ─── Act 4: Lake Legend (Finale) ───────────────────────────────────────

/**
 * Lake Legend 15: Gotta Catch 'Em All
 */
export const LAKE_SPECIES_MASTER: QuestDefinition = {
  id: 'lake_species_master',
  name: "Gotta Catch 'Em All",
  description: 'Wanda opens a weathered journal. "Mountain Lake is home to ten fish species. You\'ve met some. But have you met them ALL? Every angler who has... they see the lake differently. It sees them differently, too." She closes the journal. "Also, there\'s a nice reward."',
  type: QuestType.DISCOVER_SPECIES,
  giverNpc: 'Wanda',

  objectives: [
    {
      id: 'obj_bluegill',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch a Bluegill',
      targetValue: 1,
      filters: { speciesId: 'bluegill' },
    },
    {
      id: 'obj_perch',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch a Yellow Perch',
      targetValue: 1,
      filters: { speciesId: 'perch' },
    },
    {
      id: 'obj_brook',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch a Brook Trout',
      targetValue: 1,
      filters: { speciesId: 'brook_trout' },
    },
    {
      id: 'obj_cutthroat',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch a Cutthroat Trout',
      targetValue: 1,
      filters: { speciesId: 'cutthroat_trout' },
    },
    {
      id: 'obj_rainbow',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch a Rainbow Trout',
      targetValue: 1,
      filters: { speciesId: 'trout' },
    },
    {
      id: 'obj_brown',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch a Brown Trout',
      targetValue: 1,
      filters: { speciesId: 'brown_trout' },
    },
    {
      id: 'obj_bass',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch a Largemouth Bass',
      targetValue: 1,
      filters: { speciesId: 'bass' },
    },
    {
      id: 'obj_cuttbow',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch a Cuttbow Trout',
      targetValue: 1,
      filters: { speciesId: 'cuttbow_trout' },
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 500, description: '+500 gold' },
    { type: QuestRewardType.EXPERIENCE, value: 150, description: '+150 XP' },
  ],

  unlockConditions: {
    completedQuests: ['lake_supply_chain'],
    minFishCaught: 100,
  },
  repeatable: false,
  isTutorial: false,
  category: 'progression',
  chainId: LAKE_LEGEND_CHAIN.id,
  displayOrder: 14,
  nextQuestId: 'lake_epic_quest',
};

/**
 * Lake Legend 16: The One That Doesn't Get Away
 */
export const LAKE_EPIC_QUEST: QuestDefinition = {
  id: 'lake_epic_quest',
  name: "The One That Doesn't Get Away",
  description: 'Wanda finally stands up from her rock. "There\'s a fish in this lake... the Arctic Grayling. It has a fin like a sail and the temperament of a cat. Catching one means you truly understand Mountain Lake. Old Pete claims he caught one in \'84. Pete lies."',
  type: QuestType.CATCH_SPECIES,
  giverNpc: 'Wanda',

  objectives: [
    {
      id: 'obj_grayling',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch an Arctic Grayling',
      targetValue: 1,
      filters: { speciesId: 'arctic_grayling' },
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 500, description: '+500 gold' },
    { type: QuestRewardType.EXPERIENCE, value: 200, description: '+200 XP' },
  ],

  unlockConditions: {
    completedQuests: ['lake_species_master'],
  },
  repeatable: false,
  isTutorial: false,
  category: 'progression',
  chainId: LAKE_LEGEND_CHAIN.id,
  displayOrder: 15,
  nextQuestId: 'lake_legend_haul',
};

/**
 * Lake Legend 17: The Legend's Haul
 */
export const LAKE_LEGEND_HAUL: QuestDefinition = {
  id: 'lake_legend_haul',
  name: "The Legend's Haul",
  description: 'Marco is in tears. Happy tears. "You... you have grown so much. From bringing me one sad fish to THIS. For your final challenge as my supplier: bring me a Rare quality fish AND earn 500 gold in sales. Let us go out with a BANG. And a squeeze of lemon."',
  type: QuestType.SELL_VALUE,
  giverNpc: 'Chef Marco',

  objectives: [
    {
      id: 'obj_rare_fish',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch a Rare quality fish (or better)',
      targetValue: 1,
      filters: {
        quality: [Quality.RARE, Quality.EPIC, Quality.LEGENDARY, Quality.UNIQUE],
      },
    },
    {
      id: 'obj_earn_500',
      type: QuestObjectiveType.EARN_CURRENCY,
      description: 'Earn 500 gold from sales',
      targetValue: 500,
    },
  ],

  rewards: [
    { type: QuestRewardType.CURRENCY, value: 750, description: '+750 gold' },
    { type: QuestRewardType.INVENTORY_EXPANSION, value: 15, description: '+15 inventory slots' },
    { type: QuestRewardType.EXPERIENCE, value: 300, description: '+300 XP' },
  ],

  unlockConditions: {
    completedQuests: ['lake_epic_quest'],
  },
  repeatable: false,
  isTutorial: false,
  category: 'progression',
  chainId: LAKE_LEGEND_CHAIN.id,
  displayOrder: 16,
  nextQuestId: 'lake_legend_finale',
};

/**
 * Lake Legend 18: Beyond the Lake (FINALE — Unlocks Mountain Stream)
 */
export const LAKE_LEGEND_FINALE: QuestDefinition = {
  id: 'lake_legend_finale',
  name: 'Beyond the Lake',
  description: 'Reel is packing bags. Badly. "Dude. DUDE. We did it. We absolutely CRUSHED Mountain Lake. We went from catching bluegill with a stick to landing Arctic Grayling with style. You know what that means?" He grins. "Time to find bigger water. I heard there\'s a mountain stream just down the trail where the trout are TWICE as feisty. Let\'s GO!"',
  type: QuestType.COMPLETE_COLLECTION,
  giverNpc: 'Reel',

  objectives: [
    {
      id: 'obj_rare_trio',
      type: QuestObjectiveType.CATCH_FISH,
      description: 'Catch 3 Rare quality fish (or better)',
      targetValue: 3,
      filters: {
        quality: [Quality.RARE, Quality.EPIC, Quality.LEGENDARY, Quality.UNIQUE],
      },
    },
  ],

  rewards: [
    { type: QuestRewardType.UNLOCK_ZONE, value: 'mountain_stream', description: 'Unlock Mountain Stream!' },
    { type: QuestRewardType.CURRENCY, value: 1000, description: '+1,000 gold' },
    { type: QuestRewardType.EXPERIENCE, value: 500, description: '+500 XP' },
    { type: QuestRewardType.INVENTORY_EXPANSION, value: 20, description: '+20 inventory slots' },
  ],

  unlockConditions: {
    completedQuests: ['lake_legend_haul'],
  },
  repeatable: false,
  isTutorial: false,
  category: 'progression',
  chainId: LAKE_LEGEND_CHAIN.id,
  displayOrder: 17,
};

// ═══════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Tutorial quest definitions (Gone Fishin' chain)
 */
export const TUTORIAL_QUEST_DEFINITIONS: QuestDefinition[] = [
  TUT_FIRST_CAST,
  TUT_SWEET_SPOT,
  TUT_FIRST_SALE,
  TUT_GEAR_UP,
  TUT_BAIT_BASICS,
  TUT_BUDDY_SYSTEM,
  TUT_HAULER_HUSTLE,
  TUT_FLY_FISHING,
  TUT_TRAP_LIFE,
  TUT_DIG_DEEP,
];

/**
 * Lake Legend quest definitions (zone progression chain)
 */
export const LAKE_LEGEND_DEFINITIONS: QuestDefinition[] = [
  LAKE_TEN_AND_COUNTING,
  LAKE_SPECIES_SAMPLER,
  LAKE_QUALITY_CONTROL,
  LAKE_BULK_ORDER,
  LAKE_BIG_SPENDER,
  LAKE_TROUT_ABOUT,
  LAKE_BUDDY_BONANZA,
  LAKE_COLOR_COLLECTOR,
  LAKE_CRAW_DADDY,
  LAKE_GOLD_RUSH,
  LAKE_BASS_TO_BASS,
  LAKE_RARE_PURSUIT,
  LAKE_FLY_MASTER,
  LAKE_SUPPLY_CHAIN,
  LAKE_SPECIES_MASTER,
  LAKE_EPIC_QUEST,
  LAKE_LEGEND_HAUL,
  LAKE_LEGEND_FINALE,
];
